import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  getRefreshFailureMessage,
  persistAuthSessionEndMessage,
} from '@/shared/api/authRefreshError';
import { createApiPath } from '@/shared/api/endpoints';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://dev.api.rudalink.com';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** 401 재시도 여부 — Axios config에만 붙이는 클라이언트 전용 플래그 */
type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

type QueueItem = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let isEndingSession = false;
let failedQueue: QueueItem[] = [];

/** 세션 만료 시 로그인 이동 — 테스트에서 교체 가능 */
let redirectToLogin = () => {
  window.location.href = '/';
};

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else if (token) {
      item.resolve(token);
    }
  });
  failedQueue = [];
};

const isRefreshEndpoint = (url?: string): boolean =>
  !!url && url.includes(createApiPath.auth.refresh());

/** 로그인·로그아웃은 재발급 루프에 넣지 않는다 */
const isLoginOrLogoutEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return (
    url.includes(createApiPath.auth.login()) ||
    url.includes(createApiPath.auth.logout())
  );
};

const clearSessionAndRedirect = () => {
  if (isEndingSession) return;
  isEndingSession = true;
  localStorage.removeItem('auth-token');
  localStorage.removeItem('refresh-token');
  localStorage.removeItem('auth-storage');
  redirectToLogin();
};

/**
 * refresh 실패 = 무조건 재로그인.
 * 400 INVALID_REFRESH_TOKEN / 401 SESSION_EXPIRED·REFRESH_TOKEN_REPLAYED·INVALID_TOKEN
 * 을 상태로 나누지 않는다. 대기 중인 요청도 전부 실패시킨다.
 */
const failRefreshAndLogout = (error: unknown) => {
  processQueue(error);
  persistAuthSessionEndMessage(getRefreshFailureMessage(error));
  clearSessionAndRedirect();
};

/**
 * Refresh Token으로 Access Token 재발급.
 * apiClient interceptor를 타지 않는 별도 axios 호출로 무한 루프를 방지한다.
 */
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refresh-token');
  if (!refreshToken) {
    throw new Error('Refresh token이 없습니다');
  }

  const { data } = await axios.post<{
    token: string;
    refreshToken?: string;
  }>(
    `${API_BASE_URL}${createApiPath.auth.refresh()}`,
    { refreshToken },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 10000,
    },
  );

  const accessToken = data.token;
  if (!accessToken) {
    throw new Error('Access token 재발급에 실패했습니다');
  }

  localStorage.setItem('auth-token', accessToken);
  if (data.refreshToken) {
    localStorage.setItem('refresh-token', data.refreshToken);
  }

  return accessToken;
};

/** 테스트에서 모듈 큐 상태를 초기화할 때 사용 */
export const __resetAuthRefreshStateForTests = () => {
  isRefreshing = false;
  isEndingSession = false;
  failedQueue = [];
  redirectToLogin = () => {
    window.location.href = '/';
  };
};

export const __setRedirectToLoginForTests = (fn: () => void) => {
  redirectToLogin = fn;
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const requestUrl = originalRequest?.url;

    // refresh 자체 실패(400·401 포함)는 재시도하지 않고 즉시 로그아웃
    if (originalRequest && isRefreshEndpoint(requestUrl)) {
      failRefreshAndLogout(error);
      return Promise.reject(error);
    }

    if (isLoginOrLogoutEndpoint(requestUrl)) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // 이미 한 번 재시도한 요청이 다시 401이면 세션 만료로 처리
    if (originalRequest._retry) {
      failRefreshAndLogout(error);
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh-token');
    if (!refreshToken) {
      failRefreshAndLogout(error);
      return Promise.reject(error);
    }

    // 이미 갱신 중이면 새 토큰을 기다렸다가 원요청 재시도
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      failRefreshAndLogout(refreshError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
}

export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
  code?: string;
}
